import React, { useEffect } from "react";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import Axios from "axios";
import { Button, Table } from "rsuite";

import constants from "utils/constants";
import { formatDateWithNameOfMonth } from "utils/date-helper";
import { getModalTableHeight } from "utils/measurement-helper";

import { getErrorMessage } from "api/defaults";
import { loadPartnerHistory } from "api/partners/partners-api";

import { PartnerHistory as History } from "store/partners/partners-types";

import ModalContainer from "components/Common/Modal/ModalContainer";
import PopoverCell from "components/Common/PopoverCell/PopoverCell";

import "./PartnerHistory.less";

interface ComponentProps {
    partnerId: string;
    isOpened: boolean;
    setModalLoaderVisibility: (visible: boolean) => void;
    setErrorResponseMessage: (message: string) => void;
    setIsPartnerHistoryOpened: (isOpened: boolean) => void;
}

const PartnerHistory: React.FC<ComponentProps> = ({
    partnerId,
    isOpened,
    setModalLoaderVisibility,
    setErrorResponseMessage,
    setIsPartnerHistoryOpened,
}: ComponentProps) => {
    const cancelTokenSource = Axios.CancelToken.source();

    const { t } = useTranslation();

    const [history, setHistory] = useImmer<History[]>([]);

    const handleModalClose = () => {
        setIsPartnerHistoryOpened(false);
        cancelTokenSource.cancel();
    };

    const header = <h1>{t("partnerDetails.history.title")}</h1>;
    const footer = (
        <>
            <Button
                className={"partner-history-modal__button"}
                type="submit"
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("partnerDetails.history.close")}
            </Button>
        </>
    );

    const tableHeight = getModalTableHeight(history.length);

    const getPartnerHistory = () => {
        setModalLoaderVisibility(true);
        loadPartnerHistory(partnerId, cancelTokenSource)
            .then(([payload, status]) => {
                if (payload && status === constants.statusCode.OK) {
                    setHistory([payload]);
                } else {
                    setErrorResponseMessage(
                        getErrorMessage(String(payload), status)
                    );
                }
            })
            .catch((error: Error) => {
                const axiosError = error;
                if (!Axios.isCancel(error)) {
                    setErrorResponseMessage(axiosError.message);
                }
            })
            .then(() => {
                setModalLoaderVisibility(false);
            });
    };

    useEffect(() => {
        if (isOpened && partnerId) {
            getPartnerHistory();
        }
    }, [partnerId, isOpened]);

    return (
        <>
            <ModalContainer
                className={"partner-history-modal__container"}
                open={isOpened}
                header={header}
                footer={footer}
                onClose={handleModalClose}
            >
                <Table
                    showHeader={true}
                    virtualized={true}
                    data={history}
                    height={tableHeight}
                >
                    <Table.Column flexGrow={2}>
                        <Table.HeaderCell>
                            {t(`partnerDetails.history.createdBy`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey="creator">
                            {(rowData: History) => (
                                <PopoverCell
                                    dataKey="creator"
                                    rowData={rowData}
                                />
                            )}
                        </Table.Cell>
                    </Table.Column>

                    <Table.Column flexGrow={1.5}>
                        <Table.HeaderCell>
                            {t(`partnerDetails.history.createdOn`)}
                        </Table.HeaderCell>
                        <Table.Cell dataKey="creationDate">
                            {(rowData: History) => (
                                <>
                                    {rowData.creationDate ? (
                                        <>
                                            {formatDateWithNameOfMonth(
                                                new Date(rowData.creationDate)
                                            )}
                                        </>
                                    ) : (
                                        <>{t(`unknown`)}</>
                                    )}
                                </>
                            )}
                        </Table.Cell>
                    </Table.Column>
                </Table>
            </ModalContainer>
        </>
    );
};

export default PartnerHistory;
